import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于爱漫画 - 韩国漫画无删减漫画下拉式免费韩漫大全',
  description: '爱漫画品牌介绍，了解我们的使命、价值观以及联系方式。致力于为读者提供最优质的韩国漫画阅读体验。',
  url: 'https://aimanhua.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '爱漫画',
    url: 'https://aimanhua.com/',
  },
  about: {
    '@type': 'Organization',
    name: '爱漫画',
    description: '爱漫画汇聚无删减韩国漫画，下拉式免费韩漫大全在线阅读便捷。',
    url: 'https://aimanhua.com/',
  },
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim()) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value as string);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9pep8eb4qjhggaepsn0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const features = [
    { icon: 'ri-shield-check-line', title: '无删减内容', desc: '坚持提供原版完整韩漫内容，绝不删减任何画面和情节' },
    { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '流畅的下拉式阅读体验，无需翻页，手机和电脑都能完美适配' },
    { icon: 'ri-timer-flash-line', title: '每日更新', desc: '与多家韩国漫画平台合作，热门漫画保持每日或每周更新' },
    { icon: 'ri-user-heart-line', title: '免费阅读', desc: '致力于为读者提供免费的优质韩漫阅读服务，让更多人享受韩漫乐趣' },
  ];

  const milestones = [
    { year: '2023', title: '爱漫画诞生', desc: '爱漫画平台正式上线，以无删减韩国漫画为核心特色，为中文读者打开韩漫世界的大门。' },
    { year: '2024', title: '内容扩展', desc: '签约更多韩国漫画作者和平台，漫画数量突破5000部，月活读者超过100万。' },
    { year: '2025', title: '体验升级', desc: '推出全新下拉式阅读模式，优化移动端阅读体验，用户好评率达98%。' },
    { year: '2026', title: '持续成长', desc: '成为中文互联网领先的韩国漫画平台，月活读者突破500万，持续引进优质韩漫作品。' },
  ];

  return (
    <>
      <SEOHead
        title="关于我们"
        description="爱漫画品牌介绍 - 汇聚无删减韩国漫画，提供下拉式免费韩漫在线阅读。了解我们的使命、发展历程和联系方式。"
        keywords="爱漫画,韩国漫画平台,关于我们,韩漫品牌,联系我们"
        canonicalUrl="https://aimanhua.com/about"
        schemaJson={pageSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <section className="pb-16">
              <div className="text-center mb-12">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  关于 <span className="text-primary-500">爱漫画</span>
                </h1>
                <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                  爱漫画汇聚无删减韩国漫画，致力于为中文读者提供最优质的韩漫阅读体验。下拉式免费韩漫大全在线阅读便捷，优质韩漫作品持续上新。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-16">
                {features.map((feature) => (
                  <div key={feature.title} className="bg-background-100 rounded-xl p-5 md:p-6 text-center">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-accent-100 text-accent-500">
                      <i className={`${feature.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="pb-16 border-t border-background-200 pt-16">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  发展历程
                </h2>
                <p className="text-sm text-foreground-500">爱漫画的成长之路</p>
              </div>

              <div className="max-w-3xl mx-auto">
                <div className="relative">
                  <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-background-200"></div>
                  <div className="space-y-8">
                    {milestones.map((item, idx) => (
                      <div key={idx} className="relative pl-12 md:pl-16">
                        <div className="absolute left-2 md:left-4 top-1 w-5 h-5 rounded-full bg-accent-500 border-4 border-background-50"></div>
                        <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold mb-2 whitespace-nowrap">
                          {item.year}
                        </span>
                        <h3 className="text-lg font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="pb-16 border-t border-background-200 pt-16">
              <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-10">
                <div className="lg:w-1/2">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-6">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或直接填写右侧表单。
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-accent-100 text-accent-500">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900">电子邮箱</h4>
                        <p className="text-sm text-foreground-500">contact@aimanhua.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-accent-100 text-accent-500">
                        <i className="ri-customer-service-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900">客服QQ</h4>
                        <p className="text-sm text-foreground-500">1234567890</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-accent-100 text-accent-500">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900">办公地址</h4>
                        <p className="text-sm text-foreground-500">中国北京市朝阳区望京SOHO</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:w-1/2">
                  <div className="bg-background-100 rounded-xl p-5 md:p-6">
                    <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                    <form
                      data-readdy-form
                      id="contact-form"
                      onSubmit={handleSubmit}
                      className="space-y-3"
                    >
                      <div>
                        <label htmlFor="name" className="block text-sm text-foreground-600 mb-1">姓名</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-email" className="block text-sm text-foreground-600 mb-1">邮箱</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>

                      <div>
                        <label htmlFor="subject" className="block text-sm text-foreground-600 mb-1">主题</label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          required
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入消息主题"
                        />
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm text-foreground-600 mb-1">消息内容</label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                          placeholder="请输入您的消息（最多500字）"
                        ></textarea>
                      </div>

                      <div className="anti-spam-field">
                        <label htmlFor="phone_alt">备用电话</label>
                        <input
                          type="text"
                          id="phone_alt"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                      </button>

                      {formStatus === 'success' && (
                        <div className="p-3 bg-accent-50 border border-accent-200 rounded-lg">
                          <p className="text-sm text-accent-700 flex items-center gap-1.5">
                            <i className="ri-check-line text-base"></i>消息发送成功！我们会尽快回复您。
                          </p>
                        </div>
                      )}

                      {formStatus === 'error' && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-600 flex items-center gap-1.5">
                            <i className="ri-error-warning-line text-base"></i>
                            {formError}
                          </p>
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}